import Storyline from "@app/layouts/Article/Storyline";
import { Annotation } from "@app/layouts/Article/StorylineElements/Annotation/Annotation";
import StorylineImage from "@app/layouts/Article/StorylineElements/Image";
import ContentHubStoryListing from "@app/layouts/ContentHub/ContentHubStoryListing";
import ContentHubStoryListingLayout from "@app/layouts/ContentHub/ContentHubStoryListingLayout";
import AdSettings from "@app/utils/AdSettings";
import { mapTeaserDataToLatestProps } from "@app/utils/mapTeaserDataToProps";
import { configSite } from "@pub/config";
import {
  Ads,
  PageShare,
  ResponsiveImage,
  Typography,
} from "@sphtech/dmg-design-system";
import { NoSSR } from "@sphtech/web2-core/components";
import cx from "classnames";
import { ReactNode } from "react";
import { useLocation } from "react-router";

import styles from "./TechshowPortal.module.css";
import TechshowPortalBlock from "./TechshowPortalBlock";
import TechshowPortalBrochures from "./TechshowPortalBrochures";
import { TechshowPortalLayoutProps } from "./types";

export default function TechshowPortalLayout({
  headline,
  coverImage,
  featuredImage,
  mainBodyElements,
  sponsorImages,
  sponsorImagesLabel,
  techshowBrochureSections,
  path,
  displaySetting,
  topStories,
  listedStories,
  tags,
  brochureImages,
  filterBrands,
  filterCategories,
  filterMap,
  techshowInfos,
}: TechshowPortalLayoutProps): ReactNode {
  const { Body, DateLine, Heading } = Typography;
  const originalCrop = featuredImage
    ? featuredImage.crops.original
    : coverImage?.crops.original;
  const location = useLocation();

  const ignoreIds = [...topStories, ...listedStories]
    .map((item) => item.id)
    .filter((id): id is string => !!id);

  const tagSlugs = tags?.map((tag) => tag.slug) || [];

  return (
    <>
      <AdSettings
        //StoryData={storyData}
        pageTitle={headline || ""}
        pageType="techshowportal"
        sectionName={headline || ""}
        currentLocation={location}
        adUnits="lb1,lb2,imu1"
      />
      {originalCrop && (
        <div className={styles.headingBackgroundContainer}>
          <ResponsiveImage {...originalCrop} displayWidth={2000} />
        </div>
      )}

      <div className={cx(styles.bodyContainer, styles.gutterHorizontal)}>
        <div className={styles.sidebarContainer}>
          <div className={styles.techshowWriteup}>
            <Heading.SectionTitle>{headline}</Heading.SectionTitle>
            <Storyline
              bodyMaxWidth={800}
              path={path}
              excludeAds={true}
              elements={mainBodyElements}
            />
          </div>
          <div className={cx(styles.sidebar)}>
            <Ads.AdsSideBarDesktop />
          </div>
        </div>
        {sponsorImages && sponsorImages.length > 0 && (
          <div className={styles.sponsorImages}>
            <Heading.SubHeading2>{sponsorImagesLabel}:</Heading.SubHeading2>
            {sponsorImages.map((sponsorImage, index) => (
              <StorylineImage
                key={index}
                element={sponsorImage}
                bodyMaxWidth={300}
              />
            ))}
          </div>
        )}
        <div className={styles.pageShareOuterContainer}>
          <PageShare
            label="Share&nbsp;this"
            url={`${configSite.address}${location.pathname}`}
            title={headline}
            size={configSite.pageShare?.size}
            media={configSite.pageShare?.media}
            fill={configSite.pageShare?.fill}
          />
        </div>
      </div>

      <div
        className={cx(
          styles.techshowContainer,
          styles.bodyContainer,
          styles.gutter,
        )}
      >
        <div className={cx(styles.techshowBody)}>
          <Heading.SubHeading>Tech Shows</Heading.SubHeading>
          <div className={cx(styles.techshowInfoContainer)}>
            {techshowInfos &&
              techshowInfos.map((techshowInfo, key) => (
                <div key={key} className={cx(styles.techshowInfo)}>
                  {techshowInfo.image && (
                    <div className={styles.tecshowInfoImage}>
                      <StorylineImage
                        key={key}
                        element={techshowInfo.image}
                        bodyMaxWidth={500}
                      />
                    </div>
                  )}
                  <div className={styles.techshowInfoDateLine}>
                    <DateLine>{techshowInfo.date}</DateLine>
                  </div>
                  <div className={styles.techshowInfoName}>
                    <Heading.SubHeading2>
                      {techshowInfo.title}
                    </Heading.SubHeading2>
                  </div>
                  <div className={styles.techshowInfoDescription}>
                    {techshowInfo.paragraphs?.map((paragraph, key) => (
                      <Body key={key}>
                        <Annotation element={paragraph} />
                      </Body>
                    ))}
                  </div>
                </div>
              ))}
            <Ads.AdsSideBarMobile />
          </div>
          {techshowBrochureSections &&
            techshowBrochureSections.map((techshowBrochures, key) => (
              <TechshowPortalBrochures
                key={key}
                brochureImages={brochureImages}
                brochureSection={techshowBrochures}
                filterBrands={filterBrands}
                filterCategories={filterCategories}
                filterMap={filterMap}
                max={9}
              />
            ))}
        </div>
      </div>

      {/* {listedStories story listing} */}
      <div
        className={cx(
          styles.backgroundFill,
          styles.secondaryContentContainer,
          styles.gutterTopOnly,
        )}
      >
        <div className={styles.techshowRelated}>
          <TechshowPortalBlock
            data={{
              id: "",
              name: headline,
              path: path,
              tagSlugs: tagSlugs,
              topStories: topStories.map((teaser) =>
                mapTeaserDataToLatestProps(teaser, ""),
              ),
              displaySetting,
            }}
            from={0}
            limit={5}
          />
        </div>
        <div
          data-layout-position="midcontenthub"
          className={cx(
            styles.gutterBottom,
            styles.secondaryContentContainer,
            styles.contentBlockListingContainer,
          )}
        >
          <Ads.AdsMidContent />
          <ContentHubStoryListingLayout
            data={{
              listedStories: listedStories.map((teaser) =>
                mapTeaserDataToLatestProps(teaser, ""),
              ),
            }}
            CustomListingLayout={undefined}
          />
          <NoSSR>
            <ContentHubStoryListing
              data={{
                tagSlugs: tagSlugs.join(","),
                limit: 6,
                ignoreIds: ignoreIds,
                lb1: <Ads.AdsMidContent />,
              }}
              CustomListingLayout={undefined}
            />
          </NoSSR>
        </div>
      </div>
    </>
  );
}
